import { useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import { z } from "zod";

const UploadFileResponseSchema = z.object({
  message: z.string(),
  s3Path: z.string(),
});

const FileStatusResponseSchema = z.object({
  hasFile: z.boolean(),
  s3Path: z.string().nullable(),
  fileExistsInS3: z.boolean(),
});

const DownloadFileResponseSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number(),
});

const DeleteFileResponseSchema = z.object({
  message: z.string(),
});

type FileStatusResponse = z.infer<typeof FileStatusResponseSchema>;

interface UseWorkPaperTemplateFileOptions {
  templateId: string | null;
  onUploadSuccess?: () => void;
  onDeleteSuccess?: () => void;
}

export function useWorkPaperTemplateFile(
  options: UseWorkPaperTemplateFileOptions,
) {
  const { templateId, onUploadSuccess, onDeleteSuccess } = options;

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [fileStatus, setFileStatus] = useState<FileStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const checkFileStatus = useCallback(async () => {
    if (!templateId) {
      return null;
    }

    setChecking(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `/work-paper-templates/${templateId}/file-status`,
        method: "GET",
      });

      const parsed = FileStatusResponseSchema.parse(response);
      setFileStatus(parsed);
      return parsed;
    } catch (error) {
      console.error("Error checking file status:", error);
      const errorMsg = "Failed to check file status";
      setError(errorMsg);
      return null;
    } finally {
      setChecking(false);
    }
  }, [templateId]);

  // Upload file
  const uploadFile = useCallback(
    async (file: File, replace: boolean = false): Promise<boolean> => {
      if (!templateId) {
        toast.error("Template ID is required");
        return false;
      }

      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
        toast.error("Only Excel files (.xlsx, .xls) are allowed");
        return false;
      }

      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("File size must be less than 50MB");
        return false;
      }

      setUploading(true);
      setError(null);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const method = replace ? "PUT" : "POST";
        const response = await fetchFastApi({
          path: `/work-paper-templates/${templateId}/upload-file`,
          method,
          body: formData,
        });

        UploadFileResponseSchema.parse(response);
        toast.success(
          replace
            ? "Template file replaced successfully"
            : "Template file uploaded successfully",
        );

        await checkFileStatus();

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        return true;
      } catch (error) {
        console.error("Error uploading file:", error);
        const errorMsg = "Failed to upload template file";
        setError(errorMsg);
        toast.error(errorMsg);
        return false;
      } finally {
        setUploading(false);
      }
    },
    [templateId, checkFileStatus, onUploadSuccess],
  );

  const getDownloadUrl = useCallback(async (): Promise<string | null> => {
    if (!templateId) {
      toast.error("Template ID is required");
      return null;
    }

    setFetchingUrl(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `/work-paper-templates/${templateId}/download-file`,
        method: "GET",
      });

      const parsed = DownloadFileResponseSchema.parse(response);
      return parsed.downloadUrl;
    } catch (error) {
      console.error("Error getting download URL:", error);
      const errorMsg = "Failed to get download URL";
      setError(errorMsg);
      toast.error(errorMsg);
      return null;
    } finally {
      setFetchingUrl(false);
    }
  }, [templateId]);

  const downloadFile = useCallback(async (): Promise<boolean> => {
    if (!templateId) {
      toast.error("Template ID is required");
      return false;
    }

    setDownloading(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `/work-paper-templates/${templateId}/download-file`,
        method: "GET",
      });

      const parsed = DownloadFileResponseSchema.parse(response);
      window.open(parsed.downloadUrl, "_blank");

      toast.success("Download started");
      return true;
    } catch (error) {
      console.error("Error downloading file:", error);
      const errorMsg = "Failed to download template file";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    } finally {
      setDownloading(false);
    }
  }, [templateId]);

  const deleteFile = useCallback(async (): Promise<boolean> => {
    if (!templateId) {
      toast.error("Template ID is required");
      return false;
    }

    setDeleting(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `/work-paper-templates/${templateId}/upload-file`,
        method: "DELETE",
      });

      DeleteFileResponseSchema.parse(response);
      toast.success("Template file deleted successfully");

      await checkFileStatus();

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      const errorMsg = "Failed to delete template file";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [templateId, checkFileStatus, onDeleteSuccess]);

  return {
    uploading,
    deleting,
    checking,
    downloading,
    fetchingUrl,
    fileStatus,
    error,
    clearError,
    checkFileStatus,
    uploadFile,
    getDownloadUrl,
    downloadFile,
    deleteFile,
  };
}
